import React, { useEffect, useMemo, useState } from 'react';
import { FiMapPin, FiSave, FiX } from 'react-icons/fi';
import mileageApi from '../../utils/api/mileageApi';

const toDateInputValue = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const initialFormFromPreview = (preview) => ({
  tripDate: toDateInputValue(preview?.tripDate),
  tripType: preview?.tripType || 'HomeVisit',
  routeType: preview?.routeType || 'RoundTrip',
  startAddress: preview?.startAddress || '',
  endAddress: preview?.endAddress || '',
  totalMiles: Number(preview?.existingTotalMiles ?? preview?.totalMiles) > 0
    ? String(preview.existingTotalMiles ?? preview.totalMiles) : '',
});

export default function MileageEntryModal({ preview, sourceType, sourceReferenceId, onClose, onSaved }) {
  const [form, setForm] = useState(() => initialFormFromPreview(preview));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(initialFormFromPreview(preview));
    setError('');
  }, [preview]);

  const sourceLabel = useMemo(() => {
    if (sourceType === 'BirthEvent') return 'Birth Event';
    if (sourceType === 'Appointment') return 'Home Visit';
    return 'Clinic Travel';
  }, [sourceType]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveTrip = async (skipForNow = false) => {
    setSaving(true);
    setError('');

    try {
      await mileageApi.createTrip({
        sourceType,
        sourceReferenceId,
        tripDate: form.tripDate || preview?.tripDate,
        tripType: form.tripType,
        routeType: form.routeType,
        startAddress: form.startAddress,
        endAddress: form.endAddress,
        totalMiles: form.totalMiles === '' ? 0 : Number(form.totalMiles),
        skipForNow,
      });
      onSaved?.(skipForNow);
      onClose();
    } catch (err) {
      setError(err.message || 'Unable to save mileage.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mileage-entry-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#edf4ef] text-[#4f7a64]">
              <FiMapPin className="h-5 w-5" />
            </div>
            <div>
              <h2 id="mileage-entry-title" className="text-lg font-semibold text-gray-900">
                Mileage for {sourceLabel}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Route is pre-filled from clinic and patient addresses.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close mileage modal"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-4">
          {error && (
            <div role="alert" className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              <span className="font-medium">Date</span>
              <input
                type="date"
                value={form.tripDate}
                onChange={(event) => updateField('tripDate', event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              <span className="font-medium">Trip Type</span>
              <select
                value={form.tripType}
                onChange={(event) => updateField('tripType', event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="HomeVisit">Home Visit</option>
                <option value="BirthEvent">Birth Event</option>
                <option value="ClinicTravel">Clinic Travel</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              <span className="font-medium">Total Miles</span>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.totalMiles}
                onChange={(event) => updateField('totalMiles', event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="0.0"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              <span className="font-medium">Start Address</span>
              <textarea
                rows={3}
                value={form.startAddress}
                onChange={(event) => updateField('startAddress', event.target.value)}
                className="resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              <span className="font-medium">End Address</span>
              <textarea
                rows={3}
                value={form.endAddress}
                onChange={(event) => updateField('endAddress', event.target.value)}
                className="resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
          </div>

          <div className="rounded-lg border border-[#d8e2dc] bg-[#f7faf8] px-3 py-2 text-sm text-gray-700">
            Route: clinic to patient and back to clinic.
          </div>
        </div>

        <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => saveTrip(true)}
            disabled={saving}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
          >
            Skip for Now
          </button>
          <button
            type="button"
            onClick={() => saveTrip(false)}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#4f7a64] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#3d6350] disabled:opacity-60"
          >
            <FiSave className="h-4 w-4" />
            Save Mileage
          </button>
        </div>
      </div>
    </div>
  );
}
